import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { AutoComplete, Button, Flex, Form, Input, Modal, Select, Typography, message } from 'antd';
import { Plus, Search } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { z } from 'zod';

import {
  useArchiveSuperadminOrganization,
  useChangeSuperadminOrganizationPrice,
  useCreateSuperadminOrganization,
  useGetSuperadminPriceTiers,
  useHardDeleteSuperadminOrganization,
  useListUsers,
  useRestoreSuperadminOrganization,
  useUpdateSuperadminOrganizationProfile,
} from '../../../api';
import { formatCreditsShort } from '../../../modules/billing';
import {
  OrganizationsStatusFilterDropdown,
  OrganizationsSubscriptionFilterDropdown,
  SuperadminOrganizationA2pDrawer,
  SuperadminOrganizationBillingDrawer,
  SuperadminOrganizationMembersDrawer,
  SuperadminOrganizationsTable,
  useSuperadminOrganizationsInfiniteQuery,
} from '../../../modules/superadmin';
import { formatPriceCents } from '../../../modules/superadmin/utils/superadminBillingDisplay';
import { OrganizationsSearchParamsSchema } from '../../../modules/superadmin/utils/superadminOrganizationsSearch';
import { TableSurface } from '../../../ui/components/table-surface';
import { useDebouncedValue } from '../../../ui/hooks';
import { useLayoutInsets } from '../../../ui/providers/layout-insets';
import { ensureUrlHasProtocol } from '../../../ui/utils/url';

import type {
  ListSuperadminOrganizationsBillingStatus,
  ListSuperadminOrganizationsResponseDtoItemsItem,
} from '../../../api';
import type { SuperadminOrganizationsRecordStatusFilter } from '../../../modules/superadmin/utils/superadminOrganizationsSearch';
import type { ChangeEvent } from 'react';

const superadminCreateOrgTimezones: { value: string; label: string }[] = (() => {
  const tzs = Intl.supportedValuesOf?.('timeZone') ?? [];
  return tzs.map((tz) => {
    try {
      const offset =
        new Intl.DateTimeFormat('en-US', {
          timeZone: tz,
          timeZoneName: 'longOffset',
        })
          .formatToParts(new Date())
          .find((p) => p.type === 'timeZoneName')
          ?.value?.replace('GMT', 'UTC') ?? 'UTC';
      const name = tz.includes('/') ? tz.split('/').slice(1).join(' / ').replace(/_/g, ' ') : tz;
      return { value: tz, label: `${offset} (${name})` };
    } catch {
      return { value: tz, label: tz };
    }
  });
})();

const OrganizationsRoute = () => {
  const navigate = Route.useNavigate();
  const insets = useLayoutInsets();
  const { search, recordStatus, sortBy, sortDirection, billingStatus } = Route.useSearch();
  const effectiveRecordStatus = recordStatus ?? 'active';
  const [createForm] = Form.useForm<{
    ownerId: string;
    name: string;
    timezone: string;
    website: string;
  }>();
  const [renameForm] = Form.useForm<{ name: string }>();
  const [renameTarget, setRenameTarget] =
    useState<ListSuperadminOrganizationsResponseDtoItemsItem | null>(null);
  const [changePriceTarget, setChangePriceTarget] =
    useState<ListSuperadminOrganizationsResponseDtoItemsItem | null>(null);
  const [selectedTierPriceId, setSelectedTierPriceId] = useState<string | undefined>(undefined);
  const [prevSearch, setPrevSearch] = useState(search);
  const [searchInput, setSearchInput] = useState(search ?? '');
  if (search !== prevSearch) {
    setPrevSearch(search);
    setSearchInput(search ?? '');
  }
  const [selectedOrganization, setSelectedOrganization] =
    useState<ListSuperadminOrganizationsResponseDtoItemsItem | null>(null);
  const [selectedA2pOrg, setSelectedA2pOrg] =
    useState<ListSuperadminOrganizationsResponseDtoItemsItem | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [ownerSearchInput, setOwnerSearchInput] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchInput, 300);
  const [debouncedOwnerSearch] = useDebouncedValue(ownerSearchInput, 300);
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 768px)').matches);
  const [selectedRowKeys, setSelectedRowKeys] = useState<Set<string>>(new Set());
  const [createOrgTimezoneSearch, setCreateOrgTimezoneSearch] = useState('');

  const createOrgTimezoneOptions = useMemo(() => {
    const q = createOrgTimezoneSearch.trim().toLowerCase();
    const list = q
      ? superadminCreateOrgTimezones.filter(
          (o) => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q),
        )
      : superadminCreateOrgTimezones;
    return list.map((o) => ({ value: o.value, label: o.label }));
  }, [createOrgTimezoneSearch]);

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 768px)');
    const handleChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    const trimmed = debouncedSearch.trim() || undefined;
    const currentSearch = (search ?? '').trim() || undefined;
    if (trimmed !== currentSearch) {
      navigate({
        to: '/superadmin/organizations',
        search: (prev) => ({
          ...prev,
          search: trimmed,
        }),
        replace: true,
        resetScroll: false,
      });
    }
  }, [debouncedSearch, search, navigate]);

  const listParams = useMemo(
    () => ({
      search: search || undefined,
      recordStatus: effectiveRecordStatus,
      sortBy: sortBy ?? 'created',
      sortDirection: sortDirection ?? 'desc',
      billingStatus: billingStatus ?? undefined,
    }),
    [search, effectiveRecordStatus, sortBy, sortDirection, billingStatus],
  );

  useEffect(() => {
    queueMicrotask(() => {
      setSelectedRowKeys(new Set());
    });
  }, [search, recordStatus, sortBy, sortDirection, billingStatus]);

  const { data, isPending, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useSuperadminOrganizationsInfiniteQuery(listParams);
  const { data: ownerUsersResponse, isFetching: isOwnerUsersLoading } = useListUsers({
    search: debouncedOwnerSearch || undefined,
    recordStatus: 'active',
    sortBy: 'created',
    sortDirection: 'desc',
    skip: 0,
    take: 20,
  });
  const queryClient = useQueryClient();

  const createMutation = useCreateSuperadminOrganization({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['superadmin', 'organizations'] });
      },
    },
  });

  const archiveMutation = useArchiveSuperadminOrganization({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['superadmin', 'organizations'] });
      },
    },
  });

  const restoreMutation = useRestoreSuperadminOrganization({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['superadmin', 'organizations'] });
      },
    },
  });

  const renameMutation = useUpdateSuperadminOrganizationProfile({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['superadmin', 'organizations'] });
      },
    },
  });

  const hardDeleteMutation = useHardDeleteSuperadminOrganization({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['superadmin', 'organizations'] });
      },
    },
  });

  const { data: priceTiersData } = useGetSuperadminPriceTiers({
    query: { enabled: changePriceTarget !== null },
  });

  const changePriceMutation = useChangeSuperadminOrganizationPrice({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['superadmin', 'organizations'] });
      },
    },
  });

  const handleArchive = useCallback(
    (organizationId: string) => {
      Modal.confirm({
        title: 'Archive organization?',
        content:
          'The organization will be archived. Members will lose access until it is restored.',
        okText: 'Archive',
        okButtonProps: { danger: true, 'data-testid': 'confirm' },
        cancelText: 'Cancel',
        onOk: () => archiveMutation.mutate({ organizationId }),
      });
    },
    [archiveMutation],
  );

  const handleRestore = useCallback(
    (organizationId: string) => {
      restoreMutation.mutate({ organizationId });
    },
    [restoreMutation],
  );

  const handleOpenRename = useCallback(
    (organization: ListSuperadminOrganizationsResponseDtoItemsItem) => {
      renameForm.setFieldsValue({ name: organization.name });
      setRenameTarget(organization);
    },
    [renameForm],
  );

  const handleCloseRename = useCallback(() => {
    setRenameTarget(null);
    renameForm.resetFields();
  }, [renameForm]);

  const handleSubmitRename = useCallback(async () => {
    if (!renameTarget) {
      return;
    }
    const values = await renameForm.validateFields().catch(() => null);
    if (!values) {
      return;
    }

    const trimmed = values.name.trim();
    if (trimmed === renameTarget.name) {
      handleCloseRename();
      return;
    }
    renameMutation.mutate(
      { organizationId: renameTarget.id, data: { name: trimmed } },
      {
        onSuccess: () => {
          message.success('Organization renamed');
          handleCloseRename();
        },
        onError: () => {
          message.error('Failed to rename organization');
        },
      },
    );
  }, [renameTarget, renameForm, renameMutation, handleCloseRename]);

  const handleHardDelete = useCallback(
    (organization: ListSuperadminOrganizationsResponseDtoItemsItem) => {
      if (organization.name !== '[DELETED]') {
        return;
      }
      Modal.confirm({
        title: 'Permanently delete organization?',
        content:
          'All data for this organization will be permanently deleted and billing will be finalized. Users themselves are preserved. This action cannot be undone.',
        okText: 'Delete permanently',
        okButtonProps: { danger: true },
        cancelText: 'Cancel',
        onOk: () =>
          new Promise<void>((resolve) => {
            hardDeleteMutation.mutate(
              { organizationId: organization.id },
              {
                onSuccess: () => {
                  message.success('Organization permanently deleted');
                  resolve();
                },
                onError: () => {
                  message.error('Failed to delete organization');
                  resolve();
                },
              },
            );
          }),
      });
    },
    [hardDeleteMutation],
  );

  const handleOpenChangePrice = useCallback(
    (organization: ListSuperadminOrganizationsResponseDtoItemsItem) => {
      setChangePriceTarget(organization);
      setSelectedTierPriceId(organization.billing?.stripePriceId ?? undefined);
    },
    [],
  );

  const handleCloseChangePrice = useCallback(() => {
    setChangePriceTarget(null);
    setSelectedTierPriceId(undefined);
  }, []);

  const handleSubmitChangePrice = useCallback(() => {
    if (!changePriceTarget || !selectedTierPriceId) {
      return;
    }
    changePriceMutation.mutate(
      { organizationId: changePriceTarget.id, data: { priceId: selectedTierPriceId } },
      {
        onSuccess: () => {
          message.success('Subscription price updated');
          handleCloseChangePrice();
        },
        onError: () => {
          message.error('Failed to update price');
        },
      },
    );
  }, [changePriceTarget, selectedTierPriceId, changePriceMutation, handleCloseChangePrice]);

  const handleSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  }, []);

  const handleRecordStatusChange = useCallback(
    async (value: SuperadminOrganizationsRecordStatusFilter) => {
      await navigate({
        to: '/superadmin/organizations',
        search: (prev) => ({
          ...prev,
          recordStatus: value === 'active' ? undefined : value,
        }),
        resetScroll: false,
      });
    },
    [navigate],
  );

  const handleBillingStatusFilterChange = useCallback(
    async (value: ListSuperadminOrganizationsBillingStatus | null | undefined) => {
      await navigate({
        to: '/superadmin/organizations',
        search: (prev) => ({
          ...prev,
          billingStatus: value ?? undefined,
        }),
        resetScroll: false,
      });
    },
    [navigate],
  );

  const handleSort = useCallback(
    (columnKey: string, direction: 'asc' | 'desc') => {
      const sortByMap: Record<string, 'name' | 'members' | 'created'> = {
        name: 'name',
        membersCount: 'members',
        createdAt: 'created',
      };
      const sortBy = sortByMap[columnKey];
      if (!sortBy) {
        return;
      }
      navigate({
        to: '/superadmin/organizations',
        search: (prev) => ({
          ...prev,
          sortBy,
          sortDirection: direction,
        }),
        resetScroll: false,
      });
    },
    [navigate],
  );

  const items = useMemo(() => data?.pages.flatMap((p) => p.data.items) ?? [], [data]);

  const bulkArchiveTargets = useMemo(
    () => items.filter((row) => selectedRowKeys.has(row.id) && !row.deletedAt),
    [items, selectedRowKeys],
  );

  const bulkRestoreTargets = useMemo(
    () => items.filter((row) => selectedRowKeys.has(row.id) && row.deletedAt),
    [items, selectedRowKeys],
  );

  const handleBulkArchiveOrganizations = useCallback(() => {
    if (bulkArchiveTargets.length === 0) {
      return;
    }
    Modal.confirm({
      title: `Archive ${bulkArchiveTargets.length} organization(s)?`,
      content:
        'The selected organizations will be archived. Members will lose access until they are restored.',
      okText: 'Archive',
      okButtonProps: { danger: true, 'data-testid': 'confirm' },
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await Promise.all(
            bulkArchiveTargets.map((row) =>
              archiveMutation.mutateAsync({ organizationId: row.id }),
            ),
          );
          setSelectedRowKeys(new Set());
          message.success(`Archived ${bulkArchiveTargets.length} organization(s)`);
        } catch {
          message.error('Failed to archive some organizations');
        }

        queryClient.invalidateQueries({ queryKey: ['superadmin', 'organizations'] });
      },
    });
  }, [archiveMutation, bulkArchiveTargets, queryClient]);

  const handleBulkRestoreOrganizations = useCallback(() => {
    if (bulkRestoreTargets.length === 0) {
      return;
    }
    Modal.confirm({
      title: `Restore ${bulkRestoreTargets.length} organization(s)?`,
      content: 'Members will regain access to restored organizations.',
      okText: 'Restore',
      okButtonProps: { 'data-testid': 'confirm' },
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await Promise.all(
            bulkRestoreTargets.map((row) =>
              restoreMutation.mutateAsync({ organizationId: row.id }),
            ),
          );
          setSelectedRowKeys(new Set());
          message.success(`Restored ${bulkRestoreTargets.length} organization(s)`);
        } catch {
          message.error('Failed to restore some organizations');
        }

        queryClient.invalidateQueries({ queryKey: ['superadmin', 'organizations'] });
      },
    });
  }, [bulkRestoreTargets, queryClient, restoreMutation]);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleOpenMembers = useCallback(
    (organization: ListSuperadminOrganizationsResponseDtoItemsItem) => {
      setSelectedOrganization(organization);
    },
    [],
  );

  const handleOpenA2p = useCallback(
    (organization: ListSuperadminOrganizationsResponseDtoItemsItem) => {
      setSelectedA2pOrg(organization);
    },
    [],
  );

  const [billingOrganizationId, setBillingOrganizationId] = useState<string | null>(null);
  const handleOpenBilling = useCallback(
    (organization: ListSuperadminOrganizationsResponseDtoItemsItem) => {
      setBillingOrganizationId(organization.id);
    },
    [],
  );

  const handleOpenCreateModal = useCallback(() => {
    createForm.resetFields();
    setOwnerSearchInput('');
    setCreateOrgTimezoneSearch('');
    setIsCreateModalOpen(true);
  }, [createForm]);

  const handleCloseCreateModal = useCallback(() => {
    setIsCreateModalOpen(false);
    createForm.resetFields();
    setOwnerSearchInput('');
    setCreateOrgTimezoneSearch('');
  }, [createForm]);

  const handleOwnerSearch = useCallback((nextValue: string) => {
    setOwnerSearchInput(nextValue);
  }, []);

  const handleCreateOrganization = useCallback(async () => {
    const values = await createForm.validateFields().catch(() => null);
    if (!values) {
      return;
    }

    createMutation.mutate(
      {
        data: {
          ownerId: values.ownerId,
          name: values.name.trim(),
          timezone: values.timezone,
          website: ensureUrlHasProtocol(values.website),
        },
      },
      {
        onSuccess: () => {
          message.success('Organization created');
          handleCloseCreateModal();
        },
        onError: () => {
          message.error('Failed to create organization');
        },
      },
    );
  }, [createForm, createMutation, handleCloseCreateModal]);

  const ownerOptions =
    ownerUsersResponse?.data?.items
      .filter((user) => !user.deletedAt)
      .map((user) => ({
        value: user.id,
        label: `${user.firstName} ${user.lastName} (${user.email})`,
      })) ?? [];

  return (
    <>
      <Flex vertical style={{ height: `calc(100dvh - ${insets.top}px)`, minHeight: 0 }}>
        <Flex
          align="center"
          gap={16}
          wrap="wrap"
          style={{
            padding: isMobile ? '12px 12px' : '12px 24px',
            minHeight: 53,
            minWidth: 0,
            flexShrink: 0,
            backgroundColor: 'var(--ant-color-bg-layout)',
          }}
        >
          {isMobile ? (
            <Flex align="center" gap={8} style={{ width: '100%', minWidth: 0, flexShrink: 0 }}>
              <Input
                placeholder="Search organizations by name, website, address, or timezone..."
                prefix={<Search size={14} />}
                value={searchInput}
                onChange={handleSearchChange}
                allowClear
                style={{ flex: 1, minWidth: 0 }}
              />
              <Button
                type="primary"
                size="small"
                icon={<Plus size={12} />}
                onClick={handleOpenCreateModal}
                title="Create organization"
              />
            </Flex>
          ) : (
            <>
              <Input
                placeholder="Search organizations by name, website, address, or timezone..."
                data-testid="superadmin-organizations-search"
                prefix={<Search size={14} />}
                value={searchInput}
                onChange={handleSearchChange}
                allowClear
                style={{
                  maxWidth: 400,
                  minWidth: 0,
                }}
              />
              <Flex align="center" gap={8} style={{ marginLeft: 'auto', flexShrink: 0 }}>
                <Button type="primary" icon={<Plus size={14} />} onClick={handleOpenCreateModal}>
                  Create organization
                </Button>
              </Flex>
            </>
          )}
        </Flex>

        {selectedRowKeys.size > 0 ? (
          <Flex
            vertical={isMobile}
            align={isMobile ? 'stretch' : 'center'}
            gap={isMobile ? 8 : 12}
            wrap={isMobile ? false : 'wrap'}
            style={{
              padding: isMobile ? '8px 12px' : '8px 24px',
              flexShrink: 0,
              backgroundColor: 'var(--ant-color-fill-tertiary)',
              borderBottom: '1px solid var(--ant-color-border-secondary)',
            }}
          >
            <Typography.Text type="secondary">
              <span className="font-mono">{selectedRowKeys.size}</span> selected
            </Typography.Text>
            <Flex gap={8} style={{ width: isMobile ? '100%' : 'auto', minWidth: 0 }}>
              <Button
                size="small"
                danger
                disabled={bulkArchiveTargets.length === 0}
                onClick={handleBulkArchiveOrganizations}
                style={{ flex: isMobile ? 1 : undefined }}
              >
                Archive
              </Button>
              <Button
                size="small"
                disabled={bulkRestoreTargets.length === 0}
                onClick={handleBulkRestoreOrganizations}
                style={{ flex: isMobile ? 1 : undefined }}
              >
                Restore
              </Button>
            </Flex>
          </Flex>
        ) : null}

        <TableSurface className="table-surface--page-inset-top-nav table-surface--full-round">
          <SuperadminOrganizationsTable
            dataSource={items}
            isPending={isPending}
            isError={isError}
            onSort={handleSort}
            onOpenMembers={handleOpenMembers}
            onOpenA2p={handleOpenA2p}
            onArchive={handleArchive}
            onRestore={handleRestore}
            onRename={handleOpenRename}
            onHardDelete={handleHardDelete}
            onChangePrice={handleOpenChangePrice}
            onOpenBilling={handleOpenBilling}
            onLoadMore={handleLoadMore}
            selectedRows={selectedRowKeys}
            onSelectedRowsChange={setSelectedRowKeys}
            subscriptionFilterHeader={
              <OrganizationsSubscriptionFilterDropdown onChange={handleBillingStatusFilterChange} />
            }
            statusFilterHeader={
              <OrganizationsStatusFilterDropdown onChange={handleRecordStatusChange} />
            }
          />
        </TableSurface>
      </Flex>

      {selectedOrganization ? (
        <SuperadminOrganizationMembersDrawer
          open
          organizationId={selectedOrganization.id}
          organizationName={selectedOrganization.name}
          onClose={() => {
            setSelectedOrganization(null);
          }}
        />
      ) : null}

      <SuperadminOrganizationBillingDrawer
        open={billingOrganizationId !== null}
        organizationId={billingOrganizationId}
        onClose={() => setBillingOrganizationId(null)}
      />

      {selectedA2pOrg ? (
        <SuperadminOrganizationA2pDrawer
          open
          organizationName={selectedA2pOrg.name}
          a2pSummary={selectedA2pOrg.a2p ?? null}
          onClose={() => setSelectedA2pOrg(null)}
        />
      ) : null}

      <Modal
        open={isCreateModalOpen}
        title="Create organization"
        okText="Create"
        onOk={() => {
          void handleCreateOrganization();
        }}
        confirmLoading={createMutation.isPending}
        onCancel={handleCloseCreateModal}
        destroyOnHidden
      >
        <Form form={createForm} layout="vertical" validateTrigger="onSubmit">
          <Form.Item
            name="ownerId"
            label="Owner"
            rules={[{ required: true, message: 'Please select owner' }]}
          >
            <Select
              showSearch
              filterOption={false}
              options={ownerOptions}
              onSearch={handleOwnerSearch}
              placeholder="Search user by name or email..."
              loading={isOwnerUsersLoading}
            />
          </Form.Item>

          <Form.Item
            name="name"
            label="Organization name"
            rules={[{ required: true, message: 'Please enter organization name' }]}
          >
            <Input placeholder="Acme Inc." />
          </Form.Item>

          <Form.Item
            name="website"
            label="Website"
            rules={[
              { required: true, message: 'Please enter a website' },
              {
                validator: (_, v) =>
                  !v || z.string().url().safeParse(ensureUrlHasProtocol(v)).success
                    ? Promise.resolve()
                    : Promise.reject(new Error('Please enter a valid URL')),
              },
            ]}
          >
            <Input placeholder="example.com" />
          </Form.Item>

          <Form.Item
            name="timezone"
            label="Workspace Timezone"
            rules={[{ required: true, message: 'Please select a timezone' }]}
          >
            <AutoComplete
              options={createOrgTimezoneOptions}
              showSearch={{ filterOption: false, onSearch: setCreateOrgTimezoneSearch }}
              onSelect={() => setCreateOrgTimezoneSearch('')}
              placeholder="Search timezone..."
            >
              <Input prefix={<Search size="1em" />} />
            </AutoComplete>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={renameTarget !== null}
        title="Rename organization"
        okText="Rename"
        onOk={() => {
          void handleSubmitRename();
        }}
        confirmLoading={renameMutation.isPending}
        onCancel={handleCloseRename}
        destroyOnHidden
      >
        <Form form={renameForm} layout="vertical" validateTrigger="onSubmit">
          <Form.Item
            name="name"
            label="Organization name"
            rules={[{ required: true, message: 'Please enter organization name' }]}
          >
            <Input placeholder="Organization name" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={changePriceTarget !== null}
        title="Change subscription price"
        okText="Change price"
        okButtonProps={{
          disabled:
            !selectedTierPriceId ||
            selectedTierPriceId === (changePriceTarget?.billing?.stripePriceId ?? undefined),
        }}
        onOk={handleSubmitChangePrice}
        confirmLoading={changePriceMutation.isPending}
        onCancel={handleCloseChangePrice}
        destroyOnHidden
      >
        <Flex vertical gap={12}>
          <Typography.Text type="secondary">
            Current:{' '}
            {formatPriceCents(
              changePriceTarget?.billing?.priceAmountCents,
              changePriceTarget?.billing?.priceCurrency,
              { dropZeroCents: true },
            ) || '—'}
          </Typography.Text>
          <Select
            value={selectedTierPriceId}
            onChange={setSelectedTierPriceId}
            placeholder="Select a price tier"
            data-testid="change-price-tier"
            options={(priceTiersData?.data.tiers ?? []).map((t) => ({
              value: t.priceId,
              label: `${formatPriceCents(t.amountCents, t.currency, { dropZeroCents: true })}${t.interval ? ` / ${t.interval}` : ''} · ${formatCreditsShort(t.credits)} credits${t.state === 'hidden' ? ' · hidden' : ''}`,
            }))}
          />
          <Typography.Text type="secondary">
            No proration — the new price applies from the next billing cycle.
          </Typography.Text>
        </Flex>
      </Modal>
    </>
  );
};

export const Route = createFileRoute('/_authenticated/superadmin/organizations')({
  component: OrganizationsRoute,
  validateSearch: (params) => OrganizationsSearchParamsSchema.parse(params),
});
